import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ErrorService } from './error.service';
import { FileType, IUpload, UploadStatus } from '@monorepo/interfaces';
import { catchError, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DataUploadService {
    private baseUrl = environment.apiConfig[1].uri;
    private httpClient = inject(HttpClient);
    private errorService = inject(ErrorService);

    public getUploadLink(fileType: FileType): Observable<{ signedURL: string }> {
        return this.httpClient.get<{ signedURL: string }>(`${this.baseUrl}/upload/${fileType}/link`).pipe(
            catchError((error: Error) => {
                console.error(`Failed to get upload link for ${fileType}: ${error.message}`);
                return this.errorService.handleError<{ signedURL: string }>(`Get upload link for ${fileType}`)(error);
            })
        );
    }

    public getCurrentUploads(fileType: FileType): Observable<IUpload[]> {
        return this.httpClient.get<IUpload[]>(`${this.baseUrl}/upload`).pipe(
            map((uploads: IUpload[]) => {
                return uploads.filter(upload => upload.type === fileType);
            }),
            map(uploads =>
                uploads.sort((a, b) => {
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                })
            ),
            catchError((error: Error) => {
                console.error(`Failed to get current uploads: ${error.message}`);
                return this.errorService.handleError<IUpload[]>(`Get current uploads`)(error);
            })
        );
    }

    public uploadFile(file: File, uploadLink: string): Observable<void> {
        const headers = new HttpHeaders({
            'Content-Type': file.type || 'application/octet-stream'
        });

        return this.httpClient.put<void>(uploadLink, file, { headers }).pipe(
            catchError((error: Error) => {
                console.error(`Failed to upload file: ${error.message}`);
                return this.errorService.handleError<void>(`Upload file`)(error);
            })
        );
    }

    public abortUpload(uploadId: string): Observable<IUpload> {
        return this.httpClient.patch<IUpload>(`${this.baseUrl}/upload/${uploadId}`, {
            status: UploadStatus.USER_CANCELLED
        });
    }
}
